import { Injector } from '@angular/core';
import { Object3D } from 'three';
import { EventTrackingValue } from '../../team-apis/core/event-tracking';
import { SideViewResizeControls } from '../render/controls/side-view-resize-controls';
import { AbstractSideViewDragHandler } from './abstract-side-view-drag-handler';

export class SideViewRescaleCuboidDragHandler extends AbstractSideViewDragHandler {
  protected method = 'drag_side_panel_arrow';
  private rescaleAllFrames = this.store.state().rescaleAllFrames;
  private lastScale = this.controls.target!.scale.toArray();

  constructor(
    initialEvent: MouseEvent,
    controls: SideViewResizeControls,
    injector: Injector,
  ) {
    super(controls, initialEvent, injector);
    this.controls.startResize(initialEvent);
  }

  protected onDrag(event: MouseEvent) {
    this.controls.applyResize(this.getWorldDelta(event));
    if (this.rescaleAllFrames) {
      this.applyNewScaleToAllFrames();
    }
  }

  protected override getEventContext(): EventTrackingValue {
    return {
      ...super.getEventContext(),
      arrow: this.controls.getSelectedArrowSide(),
    };
  }

  protected override complete() {
    super.complete();
    this.controls.stopResize();
  }

  private applyNewScaleToAllFrames() {
    const delta = this.calculateAxisDelta();
    this.getFocusedShapeStore()('key_locations').setUsing((currentLocations) =>
      currentLocations.map((location) => {
        if (location.frame_number === this.store.state().render.frameNumber) {
          // The cuboid at the current frame has already been updated by
          // the controls
          return location;
        } else {
          // Perform a resize in that frame's local space (including rotation)
          // as if the same edge of the cuboid had been dragged in the same
          // side view
          const axis = this.controls.getArrowAxis();
          const translateGuide = new Object3D();
          translateGuide.position.fromArray(location.position);
          translateGuide.rotation.fromArray(location.rotation);
          translateGuide.scale.fromArray(location.scale);
          translateGuide.translateOnAxis(axis, delta / 2);

          return {
            ...location,
            position: translateGuide.position.toArray(),
            scale: this.controls.target!.scale.toArray(),
          };
        }
      }),
    );
  }

  private calculateAxisDelta() {
    // The cuboid's local axis that the drag was performed on
    const axis = this.controls
      .getArrowAxis()
      .toArray()
      .map(Math.round)
      .map(Math.abs)
      .findIndex((i) => i === 1);

    // The difference in scale on that axis
    const currentScale = this.controls.target!.scale.toArray();
    const delta = currentScale[axis] - this.lastScale[axis];
    this.lastScale = currentScale;

    return delta;
  }
}
